<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import { useSubscriptionFormStore } from "@modules/abonnement/stores/subscription-form.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Résumé de la souscription",
            offers: "Offre et options",
            total_amount_due: "Montant total dû",
            month: "mois",
            year: "an",
            modify: "Modifier",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const subscriptionFormStore = useSubscriptionFormStore()
const { getSummary, commitment } = storeToRefs(subscriptionFormStore)

// ================================
// PROPS & EMITS
// ================================
const emit = defineEmits<{
    (e: "modify"): void
}>()

// ================================
// BUSINESS LOGIC
// ================================
/** Formate un montant en devise EUR (fr-FR). */
function formatPrice(value: number): string {
    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value)
}

// ================================
// COMPUTED
// ================================
/** Montant total des détails (somme des prix mensuels). */
const getTotal = computed(() => {
    const details = getSummary.value.details
    const sum = details.reduce((acc, d) => acc + Number(d.price), 0)
    return sum.toFixed(2)
})

/** Unité d'affichage des prix (an si engagement, sinon mois). */
const getPriceUnit = computed(() => (commitment.value ? t("year") : t("month")))

/** Multiplicateur pour afficher le montant (x12 si engagement pour prix annuel). */
const getPriceMultiplier = computed(() => (commitment.value ? 12 : 1))
</script>

<template>
    <BaseCard
        class="subscription-form-summary"
        :pt="{
            header: 'mx-auto p-6',
            content:
                'flex flex-col justify-center gap-8 w-full max-w-[30.25rem] max-md:max-w-full text-primary-900',
        }"
    >
        <template #header>
            <h2 class="text-xl font-semibold text-primary-900">
                {{ t("title") }}
            </h2>
        </template>
        <template #content>
            <div class="w-full flex flex-col gap-6">
                <h3 class="text-xl font-medium text-primary-800">
                    {{ t("offers") }}
                </h3>
                <p class="font-medium text-primary-700">
                    {{ getSummary.plan }}
                </p>
                <div class="flex flex-col gap-2">
                    <div
                        v-for="(detail, index) in getSummary.details"
                        :key="index"
                        class="flex flex-col gap-2 border-l border-primary-200 pl-2 py-1"
                    >
                        <div
                            class="flex flex-row justify-between items-center w-full gap-2"
                        >
                            <p class="text-primary-800 min-w-0">
                                {{ detail.name }}
                            </p>
                            <div
                                class="flex flex-col items-end shrink-0 text-right"
                            >
                                <p
                                    class="font-medium text-primary-900 whitespace-nowrap"
                                >
                                    {{ index !== 0 ? "+ " : ""
                                    }}{{
                                        formatPrice(
                                            Number(detail.price) *
                                                getPriceMultiplier
                                        )
                                    }}
                                    / {{ getPriceUnit }}
                                </p>
                                <p
                                    v-if="commitment"
                                    class="text-sm text-primary-600"
                                >
                                    {{ formatPrice(Number(detail.price)) }} /
                                    {{ t("month") }}
                                </p>
                            </div>
                        </div>
                        <BaseButton
                            v-if="detail.allowModify"
                            :label="t('modify')"
                            link
                            size="small"
                            class="self-start p-0 text-primary-500"
                            @click="emit('modify')"
                        />
                    </div>
                </div>
            </div>

            <hr class="w-full border-t border-primary-100 h-px" />

            <div
                class="flex flex-row flex-wrap items-center justify-between w-full gap-2 max-md:flex-col max-md:items-start"
            >
                <p class="text-xl font-medium text-primary-800">
                    {{ t("total_amount_due") }}
                </p>
                <div class="flex flex-col items-end text-right">
                    <p class="text-xl font-semibold text-primary-900">
                        {{ formatPrice(Number(getTotal) * getPriceMultiplier) }}
                        / {{ getPriceUnit }}
                    </p>
                    <p v-if="commitment" class="text-sm text-primary-600">
                        {{ formatPrice(Number(getTotal)) }} / {{ t("month") }}
                    </p>
                </div>
            </div>
        </template>
    </BaseCard>
</template>
